import { useCallback, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Url, { url } from "src/consts/urlPaths";
import type { Dashboard } from "./types";

/** Owns the delete-confirmation flow (which dashboard, in-flight state, and
 * navigating away if the page being deleted is the one currently open).
 * Error state is reported to the caller rather than owned here, so it can
 * share one slot with useDashboardRename the way the pre-split component
 * did (last failure wins, whichever flow it came from). */
const useDashboardDeletion = (
  hideDashboard: (id: string) => Promise<unknown>,
  onError: (error: Error) => void
) => {
  const history = useHistory();
  const location = useLocation();

  const [dashboardToDelete, setDashboardToDelete] = useState<Dashboard | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const closeDeleteModal = useCallback(() => setDashboardToDelete(null), []);

  const confirmDelete = useCallback(async () => {
    const dashboard = dashboardToDelete;
    setIsDeleting(true);

    try {
      await hideDashboard(dashboard.id);
    } catch (deleteError) {
      // The dashboard is still there, so stay where we are and surface why.
      onError(deleteError as Error);
      return;
    } finally {
      setIsDeleting(false);
      setDashboardToDelete(null);
    }

    // Both dashboard routes end in the dashboard id, so leave the page we just
    // deleted instead of letting it fail its next fetch.
    if (location.pathname.endsWith(`/${dashboard.id}`)) {
      history.push(url(Url.APP_HOME));
    }
  }, [dashboardToDelete, hideDashboard, location.pathname, history, onError]);

  return {
    dashboardToDelete,
    requestDelete: setDashboardToDelete,
    isDeleting,
    confirmDelete,
    closeDeleteModal,
  };
};

export default useDashboardDeletion;
